// src/pages/About.jsx
import React from 'react';
import { Container, Typography, Box, Grid, Button, Paper, useTheme, alpha } from '@mui/material';
import { Bookmark, Visibility, Diamond, Security, TrendingUp, People, EmojiEvents, WorkspacePremium, Verified, FlashOn, Star, BusinessCenter, School, Handshake } from '@mui/icons-material';
import { motion } from "framer-motion";

// Professional DiamondBank color palette - Dark Gray/Black with subtle purple accent
const colors = {
  primary: '#1a1a1a',
  primaryLight: '#2d2d2d',
  primaryDark: '#0d0d0d',
  accent: '#6b21a5',
  accentLight: '#8b5cc9',
  accentDark: '#4c1d6e',
  secondary: '#e5e7eb',
  white: '#ffffff',
  black: '#000000',
  gray: '#6b7280',
  grayLight: '#9ca3af',
  grayDark: '#374151',
  lightBg: '#f8f9fa',
  textDark: '#1a1a1a',
  textLight: '#ffffff',
  gold: '#d4af37'
};

// Use public folder paths directly (no import needed for images in public folder)
const aboutHeroImage = '/images/j2.jpg';
const teamImage = '/images/db2 top (2).png';

const About = () => {
  const theme = useTheme();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } }
  };

  const slideInFromLeft = {
    hidden: { x: -50, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  const slideInFromRight = {
    hidden: { x: 50, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  const milestones = [
    { year: '2018', title: 'Bank Founded', description: 'DiamondBank established as Zimbabwe\'s premier financial institution' },
    { year: '2020', title: 'Digital Launch', description: 'Launched award-winning mobile banking platform' },
    { year: '2022', title: 'Expansion', description: 'Opened 5 new branches across Zimbabwe' },
    { year: '2024', title: 'Recognition', description: 'Named Best Bank in Zimbabwe' }
  ];

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <Box sx={{ overflowX: 'hidden', backgroundColor: colors.lightBg }}>
        {/* Hero Section - Professional Dark Theme */}
        <motion.div
          variants={fadeIn}
          style={{
            height: '60vh',
            minHeight: '450px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: `linear-gradient(135deg, ${colors.primary}dd 0%, ${colors.accent}99 100%), url(${aboutHeroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            color: 'white',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Animated background pattern */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.08,
            backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.white} 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }} />

          <Container>
            <motion.div variants={itemVariants}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Diamond sx={{ fontSize: 70, color: colors.accent, animation: 'spin 10s linear infinite' }} />
              </Box>
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                About <Box component="span" sx={{ color: colors.accent }}>DiamondBank</Box>
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  maxWidth: '700px',
                  mx: 'auto',
                  textShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  fontSize: { xs: '1rem', md: '1.3rem' },
                  fontWeight: 400,
                  opacity: 0.95,
                  lineHeight: 1.6
                }}
              >
                Zimbabwe's Premier Financial Institution for Innovation, Integrity & Excellence
              </Typography>
            </motion.div>
          </Container>
        </motion.div>

        <Container sx={{ py: 10 }}>
          {/* Introduction Section */}
          <Grid container spacing={6} sx={{ mb: 10 }}>
            <Grid item xs={12} md={6}>
              <motion.div variants={slideInFromLeft} viewport={{ once: true }}>
                <Typography
                  variant="h2"
                  gutterBottom
                  sx={{
                    color: colors.primary,
                    fontWeight: 700,
                    mb: 3,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -10,
                      left: 0,
                      width: '60px',
                      height: '3px',
                      backgroundColor: colors.accent,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Our Story
                </Typography>
                <Typography
                  variant="body1"
                  paragraph
                  sx={{
                    fontSize: '1.05rem',
                    lineHeight: 1.7,
                    mb: 3,
                    color: colors.gray
                  }}
                >
                  DiamondBank is Zimbabwe's premier financial institution, established with a vision to revolutionize banking through innovation, integrity, and exceptional customer service.
                </Typography>
              
                <Typography variant="body1" paragraph sx={{ fontSize: '1.05rem', lineHeight: 1.7, fontWeight: 500, color: colors.accent }}>
                  Our mission is to bring world-class banking services to every Zimbabwean, fostering financial inclusion and economic development across Zimbabwe.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, mt: 4, flexWrap: 'wrap' }}>
                  <Button
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    variant="contained"
                    size="large"
                    href="/careers"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '0.95rem',
                      borderRadius: '50px',
                      background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentDark})`,
                      boxShadow: `0 4px 12px ${alpha(colors.accent, 0.3)}`,
                      transition: 'all 0.3s ease',
                      fontWeight: 600,
                      '&:hover': {
                        background: colors.accentLight,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 16px ${alpha(colors.accent, 0.4)}`
                      }
                    }}
                  >
                    Join Our Team
                  </Button>
                  <Button
                    component={motion.button}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    variant="outlined"
                    size="large"
                    href="/contact"
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: '0.95rem',
                      borderRadius: '50px',
                      borderWidth: '2px',
                      borderColor: colors.accent,
                      color: colors.accent,
                      fontWeight: 600,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: colors.accentLight,
                        color: colors.accentLight,
                        transform: 'translateY(-2px)',
                        backgroundColor: alpha(colors.accent, 0.05)
                      }
                    }}
                  >
                    Contact Us
                  </Button>
                </Box>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                variants={slideInFromRight}
                viewport={{ once: true }}
                whileHover={{
                  scale: 1.02,
                  boxShadow: `0 30px 60px -10px ${alpha(colors.accent, 0.3)}`
                }}
                style={{
                  position: 'relative',
                  borderRadius: '20px',
                  overflow: 'hidden',
                  height: '100%',
                  minHeight: 350,
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                }}
              >
                <Box
                  component="img"
                  src={teamImage}
                  alt="DiamondBank Team"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: 3,
                  background: `linear-gradient(to top, ${colors.primary}, transparent)`,
                  color: 'white',
                  zIndex: 2,
                  textAlign: 'left'
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Our Leadership Team</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>Visionary leaders driving financial innovation</Typography>
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          
          {/* Vision and Mission Section */}
          <Box sx={{ mb: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <motion.div variants={itemVariants} viewport={{ once: true }}>
                <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1 }}>
                  OUR GUIDING PRINCIPLES
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: colors.primary,
                    position: 'relative',
                    display: 'inline-block',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -15,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '100px',
                      height: '3px',
                      background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Vision & Mission
                </Typography>
              </motion.div>
            </Box>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.5, type: "spring", stiffness: 100 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '20px',
                      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                      color: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 20px 40px ${alpha(colors.primary, 0.3)}`
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={3}>
                      <Box
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: '50%',
                          backgroundColor: alpha(colors.accent, 0.2),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          color: colors.accent
                        }}
                      >
                        <Visibility sx={{ fontSize: 35 }} />
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '1.8rem' }}>
                        Our Vision
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.7, opacity: 0.9 }}>
                      To be Zimbabwe's most trusted and innovative financial partner, setting the standard for banking excellence across Africa.
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={6}>
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.5, type: "spring", stiffness: 100 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      height: '100%',
                      borderRadius: '20px',
                      background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
                      color: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `0 20px 40px ${alpha(colors.accent, 0.3)}`
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={3}>
                      <Box
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: '50%',
                          backgroundColor: alpha(colors.white, 0.2),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          color: colors.white
                        }}
                      >
                        <Bookmark sx={{ fontSize: 35 }} />
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, fontSize: '1.8rem' }}>
                        Our Mission
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.7, opacity: 0.9 }}>
                      To empower our customers with innovative financial solutions, drive economic growth through strategic partnerships, and create lasting value for all stakeholders.
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            </Grid>
          </Box>

          {/* Core Values Section */}
          <Box sx={{ mb: 10 }}>
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <motion.div variants={itemVariants} viewport={{ once: true }}>
                <Typography variant="subtitle1" sx={{ color: colors.accent, fontWeight: 600, letterSpacing: 1, mb: 1 }}>
                  OUR FOUNDATION
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: colors.primary,
                    position: 'relative',
                    display: 'inline-block',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -15,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '100px',
                      height: '3px',
                      background: `linear-gradient(90deg, ${colors.primary}, ${colors.accent})`,
                      borderRadius: '2px'
                    }
                  }}
                >
                  Core Values
                </Typography>
                <Typography variant="h5" sx={{ mt: 3, color: colors.gray, fontWeight: 400, fontSize: '1.1rem' }}>
                  The principles that guide everything we do
                </Typography>
              </motion.div>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {[
                {
                  icon: <Diamond sx={{ fontSize: 35, color: colors.accent }} />,
                  title: "Excellence",
                  description: "We strive for excellence in everything we do, delivering superior quality and value to our customers."
                },
                {
                  icon: <Security sx={{ fontSize: 35, color: colors.accent }} />,
                  title: "Integrity",
                  description: "We operate with unwavering integrity, transparency, and ethical conduct at all times."
                },
                {
                  icon: <TrendingUp sx={{ fontSize: 35, color: colors.accent }} />,
                  title: "Innovation",
                  description: "We embrace innovation and technology to create better banking solutions for our customers."
                },
                {
                  icon: <People sx={{ fontSize: 35, color: colors.accent }} />,
                  title: "Customer First",
                  description: "Our customers are at the heart of everything we do, and we are committed to their success."
                },
                {
                  icon: <Handshake sx={{ fontSize: 35, color: colors.accent }} />,
                  title: "Partnership",
                  description: "We build lasting partnerships based on trust, mutual respect, and shared success."
                }
              ].map((value, index) => (
                <Grid item xs={12} sm={6} md={4} key={`value-${index}`}>
                  <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    whileHover={{ y: -5 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: '16px',
                        background: 'white',
                        boxShadow: `0 4px 16px ${alpha(colors.primary, 0.05)}`,
                        border: `1px solid ${alpha(colors.gray, 0.1)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 12px 28px ${alpha(colors.accent, 0.1)}`,
                          borderColor: alpha(colors.accent, 0.2)
                        },
                        textAlign: 'center',
                        height: '100%'
                      }}
                    >
                      <Box
                        sx={{
                          width: 70,
                          height: 70,
                          borderRadius: '50%',
                          backgroundColor: alpha(colors.accent, 0.1),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          mx: 'auto',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: alpha(colors.accent, 0.2),
                            transform: 'scale(1.05)'
                          }
                        }}
                      >
                        {value.icon}
                      </Box>
                      <Typography variant="h5" gutterBottom sx={{ color: colors.primary, fontWeight: 700, fontSize: '1.3rem' }}>
                        {value.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.gray, lineHeight: 1.6 }}>
                        {value.description}
                      </Typography>
                    </Paper>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Statistics Section */}
          <Box sx={{
            mt: 6,
            p: { xs: 4, md: 6 },
            borderRadius: '24px',
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
            color: 'white',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: `radial-gradient(circle at 20% 50%, ${colors.accent} 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
              opacity: 0.1
            }
          }}>
            <Grid container spacing={4}>
              {[
                { number: "", label: "Years of Excellence", icon: <Star sx={{ fontSize: 30 }} /> },
                { number: "", label: "Happy Customers", icon: <People sx={{ fontSize: 30 }} /> },
                { number: "", label: "Assets Under Management", icon: <BusinessCenter sx={{ fontSize: 30 }} /> },
                { number: "", label: "Customer Support", icon: <Verified sx={{ fontSize: 30 }} /> }
              ].map((stat, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, type: "spring", stiffness: 200 }}
                  >
                    <Box sx={{ color: colors.accent, mb: 1 }}>{stat.icon}</Box>
                    <Typography variant="h2" sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.85 }}>
                      {stat.label}
                    </Typography>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* CTA Section */}
          <Box sx={{
            mt: 8,
            p: { xs: 4, md: 6 },
            borderRadius: '20px',
            background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentDark} 100%)`,
            color: 'white',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <Box sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: '50%',
              background: alpha(colors.white, 0.1),
              filter: 'blur(30px)'
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: -50,
              left: -50,
              width: 150,
              height: 150,
              borderRadius: '50%',
              background: alpha(colors.white, 0.1),
              filter: 'blur(30px)'
            }} />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 2, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                Become Part of Our Story
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
                Join thousands of satisfied customers who have chosen DiamondBank for their financial needs
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  variant="contained"
                  size="large"
                  href="/personal"
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    backgroundColor: colors.white,
                    color: colors.accent,
                    fontWeight: 700,
                    '&:hover': { backgroundColor: colors.secondary, transform: 'translateY(-2px)' }
                  }}
                >
                  Open an Account
                </Button>
                <Button
                  component={motion.button}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  variant="outlined"
                  size="large"
                  href="/contact"
                  sx={{
                    px: 5,
                    py: 1.5,
                    fontSize: '1rem',
                    borderRadius: '50px',
                    borderWidth: '2px',
                    borderColor: colors.white,
                    color: colors.white,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: alpha(colors.white, 0.1), transform: 'translateY(-2px)' }
                  }}
                >
                  Learn More
                </Button>
              </Box>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* CSS animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </motion.div>
  );
};

export default About;